import { FactionId } from '@neon-maw/shared';

export interface PersistentProfileV1 {
  totalRuns: number;
  highScore: number;
  totalScore: number;
  factionReputation: Record<FactionId, number>;
  unlockedItems: string[];
  equippedItems: string[];
}

export interface PersistentProfile extends PersistentProfileV1 {
  version: number;
  // future fields can be added here
}

export const CURRENT_VERSION = 2;

export const DEFAULT_PROFILE: PersistentProfile = {
  version: CURRENT_VERSION,
  totalRuns: 0,
  highScore: 0,
  totalScore: 0,
  factionReputation: {
    maw_corps: 0,
    neon_syndicate: 0,
    scrappers: 0
  },
  unlockedItems: ['basic_dash'],
  equippedItems: ['basic_dash']
};

const STORAGE_KEY = 'neon_maw_profile_data';
const LEGACY_STORAGE_KEY = 'neon_maw_profile_v1';

export function migrateProfile(data: any): PersistentProfile {
  let profile = { ...data };

  // If no version is specified but we have data, assume v1
  if (!profile.version) {
    profile.version = 1;
  }

  // Run migrations
  if (profile.version === 1) {
    // Migrate v1 to v2
    profile.version = 2;
    // (Add any new v2 fields here if needed in the future)
  }

  // More migrations could be added here
  // if (profile.version === 2) { profile.version = 3; ... }

  return profile as PersistentProfile;
}

export function loadProfile(): PersistentProfile {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    
    // Fallback to legacy key for existing users
    if (!raw) {
      raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (raw) {
        // If we found legacy data, load it and save it to new key right away
        const migrated = migrateProfile(JSON.parse(raw));
        saveProfile(migrated);
        return migrated;
      }
      
      // If no data exists at all, initialize the store with default
      saveProfile(DEFAULT_PROFILE);
      return { ...DEFAULT_PROFILE };
    }

    if (raw) {
      return migrateProfile(JSON.parse(raw));
    }
  } catch (e) {
    console.warn('Failed to load profile from local storage', e);
  }
  return { ...DEFAULT_PROFILE };
}

export function saveProfile(profile: PersistentProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (e) {
    console.warn('Failed to save profile', e);
  }
}

export function resetProfile(): PersistentProfile {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (e) {
    console.warn('Failed to reset profile', e);
  }
  return { ...DEFAULT_PROFILE };
}

export function exportProfileBase64(): string {
  const profile = loadProfile();
  return btoa(JSON.stringify(profile));
}

export function importProfileBase64(base64Data: string): PersistentProfile {
  try {
    const raw = atob(base64Data);
    const parsed = JSON.parse(raw);
    const migrated = migrateProfile(parsed);
    saveProfile(migrated);
    return migrated;
  } catch (e) {
    console.error('Failed to import profile', e);
    throw new Error('Invalid profile data');
  }
}
